import type { OnChangeValue, Option } from '@ksyos/design-system';
import { AsyncSelect } from '@ksyos/design-system';
import { useState } from 'react';
import type { MatchType, OrganizationType } from '@common/enums';
import type { IOrganizationFilters } from '@common/validation/organization';
import { ActiveOrInactive, OrganizationRelationshipType, ServiceCode } from '@common/enums';
import { OrganizationStructureType } from '@common/enums/organizationStructureType';
import { getSelectedLanguage } from '@frontend/translate';
import { getValidationErrorTranslation, useAbortableFetch } from '@frontend/utils';
import type { IOrganization } from '../interfaces/organization';

interface OrganizationSelectionProps {
    defaultOptions?: boolean | Option[];
    error?: string;
    excludeOrganizationIds?: string[];
    hasActiveTcohAnalyst?: boolean;
    id?: string;
    isActive?: boolean;
    isClearable?: boolean;
    isKsyosOrganizationExcluded?: boolean;
    matchType?: MatchType;
    onChange: (value?: string, label?: string) => Promise<void>;
    onDoValidate?: () => Promise<void>;
    onMenuOpen?: () => void;
    organizationRelationshipType?: OrganizationRelationshipType;
    organizationStructureType?: OrganizationStructureType;
    organizationType?: string;
    placeholder?: string;
    showCity?: boolean;
    value?: string;
}

export function OrganizationSelection(props: Readonly<OrganizationSelectionProps>): JSX.Element {
    const [options, setOptions] = useState<Option[]>([]);
    const abortableFetch = useAbortableFetch();

    return (
        <AsyncSelect
            cacheOptions={props.organizationRelationshipType || true}
            defaultOptions={props.defaultOptions}
            error={getValidationErrorTranslation(props.error)}
            filterOption={null}
            id={props.id}
            isClearable={props.isClearable ?? true}
            loadOptions={loadOptions}
            noOptionsMessage={() => getSelectedLanguage().common.typeToSearch}
            onChange={handleChange}
            onMenuOpen={props.onMenuOpen}
            value={props.value ? options.find((option) => option.value === props.value) : undefined}
        />
    );

    async function loadOptions(input?: string): Promise<Option[]> {
        const filters = setFilters(input);

        const result = await abortableFetch.fetchJson('/api/organizations/list', 'POST', { filters });
        const loadedOptions: Option[] = result.json.items
            .filter((item: IOrganization) => !props.excludeOrganizationIds?.includes(item.id))
            .map((item: IOrganization) => ({
                label: `${item.name}${props.showCity && item.city ? `, ${item.city}` : ''}`,
                value: item.id,
            }));

        setOptions(loadedOptions);

        return loadedOptions;
    }

    function setFilters(input?: string) {
        const filters: IOrganizationFilters = {};
        if (props.organizationType) {
            filters.organizationTypes = [props.organizationType as OrganizationType];
        }

        if (props.isActive) {
            filters.isActive = ActiveOrInactive.active;
        }

        if (props.isKsyosOrganizationExcluded) {
            filters.isKsyosOrganizationExcluded = true;
        }

        if (props.hasActiveTcohAnalyst) {
            filters.providedService = ServiceCode.TCOH;
            filters.hasActiveAnalyst = true;
        }

        if (props.matchType) {
            filters.matchType = props.matchType;
        }

        if (props.organizationRelationshipType === OrganizationRelationshipType.isBranchOf) {
            filters.organizationStructureTypes = [OrganizationStructureType.organization];
        } else if (props.organizationRelationshipType === OrganizationRelationshipType.isDuplicateOf) {
            filters.organizationStructureTypes = Object.values(OrganizationStructureType);
        }

        if (input) {
            filters.searchText = input;
        } else if (props.value) {
            filters.include = props.value;
        }

        return filters;
    }

    async function handleChange(newValue: OnChangeValue<Option, false> | undefined) {
        if (newValue) {
            await props.onChange(newValue.value, newValue.label);
        } else {
            await props.onChange(undefined);
        }
    }
}
